import { useState } from 'react';
import { contactInfo } from '@/mocks/homeData';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const companyAlt = formData.get('company_alt');
    if (companyAlt && typeof companyAlt === 'string' && companyAlt.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'company_alt') {
        payload.append(key, value.toString());
      }
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d96vj97pg5pqhofcutug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_) { /* ignore parse error */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || 'Submission failed. Please try again.';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="section-container">
        <div className="text-center mb-14 animate-on-scroll">
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-5">
            Contact Us
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-800 mb-3">
            Let&apos;s Start a<br />Conversation
          </h2>
          <p className="text-foreground-500 max-w-[560px] mx-auto leading-relaxed">
            Whether you have an inquiry, need a quote, or want to explore partnership opportunities — we&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div className="animate-on-scroll">
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 text-sm border border-[#E6EAF0] rounded-md bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors placeholder:text-foreground-300"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 text-sm border border-[#E6EAF0] rounded-md bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors placeholder:text-foreground-300"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="company" className="block text-xs font-medium text-foreground-700 mb-1.5">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 text-sm border border-[#E6EAF0] rounded-md bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors placeholder:text-foreground-300"
                    placeholder="Your company name"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-medium text-foreground-700 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-4 py-3 text-sm border border-[#E6EAF0] rounded-md bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors placeholder:text-foreground-300"
                    placeholder="+ Country code"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 mb-1.5">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 text-sm border border-[#E6EAF0] rounded-md bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors placeholder:text-foreground-300"
                  placeholder="How can we help you?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  className="w-full px-4 py-3 text-sm border border-[#E6EAF0] rounded-md bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none placeholder:text-foreground-300"
                  placeholder="Tell us about your requirements..."
                ></textarea>
                <p className="text-[11px] text-foreground-400 mt-1">Maximum 500 characters</p>
              </div>

              <div className="form-honeypot">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <button
                type="submit"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 bg-primary-500 text-white text-sm font-heading font-semibold rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                Send Inquiry <i className="ri-send-plane-line ml-2"></i>
              </button>

              {formStatus === 'success' && (
                <div className="p-4 rounded-md bg-green-50 border border-green-200 text-sm text-green-700">
                  Thank you for your inquiry. Our team will get back to you shortly.
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                  {formError}
                </div>
              )}
            </form>
          </div>

          <div className="animate-on-scroll animate-on-scroll-delay-2">
            <div className="bg-[#F7F9FC] rounded-lg p-7 mb-6">
              <h3 className="text-base font-heading font-semibold text-foreground-800 mb-4">Address</h3>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-primary-50 flex items-center justify-center">
                  <i className="ri-map-pin-line text-primary-500 text-sm"></i>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{contactInfo.factoryAddress}</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-primary-50 flex items-center justify-center">
                  <i className="ri-building-line text-primary-500 text-sm"></i>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{contactInfo.officeAddress}</p>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-primary-50 flex items-center justify-center">
                  <i className="ri-global-line text-primary-500 text-sm"></i>
                </div>
                <a href="https://www.beaumondetex.com" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-500 hover:underline">{contactInfo.website}</a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-3 p-4 bg-[#F7F9FC] rounded-lg hover:bg-background-200 transition-colors">
                <div className="w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center">
                  <i className="ri-phone-line text-primary-500"></i>
                </div>
                <div>
                  <div className="text-[11px] text-foreground-400">Phone</div>
                  <div className="text-xs font-medium text-foreground-700">+880 1674-283025</div>
                </div>
              </a>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 p-4 bg-[#F7F9FC] rounded-lg hover:bg-background-200 transition-colors">
                <div className="w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center">
                  <i className="ri-mail-line text-primary-500"></i>
                </div>
                <div>
                  <div className="text-[11px] text-foreground-400">Email</div>
                  <div className="text-xs font-medium text-foreground-700">{contactInfo.email}</div>
                </div>
              </a>
            </div>

            <div className="rounded-lg overflow-hidden border border-[#E6EAF0]">
              <iframe
                src={contactInfo.googleMapsEmbed}
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BA Fashionwear factory location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}